import type { ReactNode } from 'react';
import { X } from 'lucide-react';

interface WizardModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Footer with action button — pass null to hide */
  footer?: ReactNode;
}

export default function WizardModal({ open, onClose, title, children, footer }: WizardModalProps) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[440px] z-[61] flora-menu-appear">
        <div className="bg-gray-50 md:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-teal-600 to-teal-400 text-white shrink-0">
            <div className="flex items-center gap-2">
              <img src="/nico-transparent.svg" alt="Nico" className="w-5 h-5" />
              <span className="font-display font-semibold text-sm">{title}</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto px-4 py-4 flex-1 space-y-4">
            {children}
          </div>

          {/* Footer */}
          {footer !== null && (
            <div className="px-4 py-3 border-t border-gray-200 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Shared sub-components ──

export function WizardButton({ onClick, disabled, loading, children }: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50"
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Wird verarbeitet...</span>
        </span>
      ) : children}
    </button>
  );
}

export function WizardLabel({ children }: { children: ReactNode }) {
  return (
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{children}</label>
  );
}

export function WizardInput({ value, onChange, placeholder, type = 'text', ...rest }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  [key: string]: unknown;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-gray-200 rounded-lg text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500"
      {...rest}
    />
  );
}

export function WizardSelect({ value, onChange, options, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:ring-2 focus:ring-emerald-500"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function WizardSuccess({ message, onDone }: { message: string; onDone: () => void }) {
  return (
    <div className="text-center py-6">
      <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
        <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="text-sm text-gray-700 font-medium">{message}</p>
      <button onClick={onDone} className="mt-4 px-6 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
        Fertig
      </button>
    </div>
  );
}

export function WizardError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
      <p>{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-2 text-xs font-medium text-red-600 underline">
          Erneut versuchen
        </button>
      )}
    </div>
  );
}
